import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { CountdownBar } from '@/presentation/components/CountdownBar';
import { AuthMethodSwitcher } from '@/presentation/components/AuthMethodSwitcher';
import { useClock } from '@/shared/hooks/useClock';
import { formatTimeWithSeconds } from '@/shared/utils/datetime';
import { KIOSK_AUTO_RESET_SECONDS } from '@/shared/config/terminal.config';
import type { AttendanceAction, VerificationMethod } from '../../domain/entities/Timelog';

const ADMIN_RESET_SECONDS = 5;

/**
 * Lookup map: which route should the "Reintentar" button send the user to,
 * based on the auth method that just failed? Mirrors AuthMethodSwitcher's
 * own internal route table to keep retry destinations consistent across the
 * error page and the in-flow switcher.
 *
 * Origin contract (audited in Slice B):
 *  - ScanningPage  → state.method = 'facial' (src/.../ScanningPage.tsx:157)
 *  - CardScanPage  → state.method = 'nfc'    (src/.../CardScanPage.tsx:65,75,112)
 *  - IdInputPage   → does NOT route to /error (PIN failures stay inline by design)
 *
 * The 'pin' entry is defensive — IdInputPage never reaches this page today,
 * but the map MUST cover every VerificationMethod union member so the lookup
 * stays exhaustive if PIN ever joins the error-routing flow.
 *
 * Spec: marking-error-retry-routing R1, R2, R3.
 */
const METHOD_ROUTE: Record<VerificationMethod, (a: AttendanceAction) => string> = {
  facial: (a) => `/scan/${a}`,
  pin:    (a) => `/id-input/${a}`,
  nfc:    (a) => `/card-scan/${a}`,
};

export function MarkingErrorPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    action?: AttendanceAction;
    method?: VerificationMethod;
    // Optional reason code propagated by capture pages when the failure has
    // a known cause. Used to pick specific error copy:
    //   duplicate_timelog      → "Marcaje duplicado, reintente más tarde."
    //   online_reject          → generic backend rejection while online
    //   consecutive_offline    → offline guard: same accessType within 2 min
    //   undefined              → generic identity error (facial/card lookup failed)
    errorCode?: 'duplicate_timelog' | 'online_reject' | 'consecutive_offline';
    /** Seconds elapsed since the last timelog (only for consecutive_offline). */
    elapsedSeconds?: number;
    /** Minimum required seconds (only for consecutive_offline). */
    minSeconds?: number;
  } | null;
  const action = state?.action ?? 'entrada';
  // Design: read method from navigation state; default to 'facial' when absent (e.g. CardScanPage
  // error path carries method:'nfc', ScanningPage carries method:'facial'). If state.method is
  // missing (direct navigation or legacy callers), fall back to 'facial'.
  const method: VerificationMethod = (state?.method ?? 'facial') as VerificationMethod;
  const errorCode = state?.errorCode;
  // Slice B (marking-error-retry-routing R1, R3): resolve the retry route from
  // the method that failed. When state.method is missing the fallback is the
  // face flow + a developer warning — this matches the legacy hardcoded
  // behavior so direct navigations to /error don't break, while surfacing the
  // missing contract for callers that should be propagating method.
  if (state?.method === undefined || state?.method === null) {
    console.warn(
      '[MarkingErrorPage] state.method missing — falling back to facial retry. ' +
      'Origin pages must propagate { action, method } on /error navigations.',
    );
  }
  const retryRoute = METHOD_ROUTE[method](action);
  const now = useClock();
  const isAdmin = action === 'acceso-admin';
  const resetSec = isAdmin ? ADMIN_RESET_SECONDS : KIOSK_AUTO_RESET_SECONDS;

  useEffect(() => {
    const timer = setTimeout(() => { void navigate('/', { replace: true }); }, resetSec * 1000);
    return () => { clearTimeout(timer); };
  }, [navigate, resetSec]);

  return (
    <main style={{ flex: 1, background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px' }}>
      <div className="mi-anim-slide-up" style={{ flex: 1, width: '100%', maxWidth: 407, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {/* X icon */}
        <div className="mi-anim-check-pop" style={{ marginBottom: 23 }}>
          <div style={{ width: 99, height: 99, borderRadius: '50%', background: `${C.danger}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 75, height: 75, borderRadius: '50%', background: C.danger, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={33} height={33} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 23, fontWeight: 700, color: T.clockText, marginBottom: 7 }}>
          {errorCode === 'consecutive_offline' ? t('error.consecutiveOfflineTitle') : t('error.title')}
        </div>
        <p style={{ fontSize: 12, fontWeight: 700, color: T.text, textAlign: 'center', lineHeight: 1.7, marginBottom: 7, maxWidth: 330 }}>
          {errorCode === 'duplicate_timelog'
            ? t('error.duplicateTimelog')
            : errorCode === 'online_reject'
              ? t('error.onlineReject')
              : errorCode === 'consecutive_offline' && state?.elapsedSeconds !== undefined && state?.minSeconds !== undefined
                ? t('error.consecutiveOffline', { 
                    elapsed: state.elapsedSeconds, 
                    remaining: state.minSeconds - state.elapsedSeconds 
                  })
                : (<>{t('error.message')}<br />{t('error.hint')}</>)}
        </p>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 26 }}>{formatTimeWithSeconds(now)}</div>

        {/* Countdown */}
        <div style={{ width: '100%', marginBottom: 23 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 500 }}>{t('error.countdown')}</span>
            <span style={{ fontSize: 10, color: T.textMuted }}>{resetSec}s</span>
          </div>
          <CountdownBar seconds={resetSec} color={C.danger} T={T} />
        </div>

        {isAdmin ? (
          <>
            <button
              onClick={() => { void navigate(retryRoute, { replace: true }); }}
              style={{ padding: '11px 28px', borderRadius: 9, border: 'none', background: C.primary, color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 16 }}
            >
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
              {t('error.retryFace')}
            </button>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '.08em', marginBottom: 10 }}>{t('error.otherMethod')}</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
              <button onClick={() => { void navigate('/id-input/acceso-admin', { replace: true }); }} style={{ padding: '9px 20px', borderRadius: 9, border: `1px solid ${C.blue}40`, background: `${C.blue}12`, color: C.blue, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                {t('error.btnPin')}
              </button>
              <button onClick={() => { void navigate('/card-scan/acceso-admin', { replace: true }); }} style={{ padding: '9px 20px', borderRadius: 9, border: `1px solid ${C.blue}40`, background: `${C.blue}12`, color: C.blue, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M16 12h.01M13 12h.01"/></svg>
                {t('error.btnCard')}
              </button>
            </div>
            <button onClick={() => { void navigate('/', { replace: true }); }} style={{ padding: '8px 22px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'transparent', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, color: T.textMuted, cursor: 'pointer' }}>
              {t('error.returnMenu')}
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 14 }}>
              <button onClick={() => { void navigate(retryRoute, { replace: true }); }} style={{ padding: '10px 32px', borderRadius: 9, border: 'none', background: C.primary, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                {t('error.retry')}
              </button>
              <button onClick={() => { void navigate('/', { replace: true }); }} style={{ padding: '10px 24px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'transparent', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: T.text, cursor: 'pointer' }}>
                {/* Copy canónico del mockup — "Volver al menú" en lugar de
                    "Cancelar". `error.returnMenu` ya existía en el bundle
                    i18n; solo se cambia la referencia (HU-D del audit). */}
                {t('error.returnMenu')}
              </button>
            </div>
            {/* AuthMethodSwitcher: shows 2 alternate auth methods based on which method failed.
                Spec: "always show 2 alternate methods in attendance flow". replace=true because
                the user must replace the error page in history to avoid back-navigation loops. */}
            <AuthMethodSwitcher
              currentMethod={method}
              action={action}
              accent={C.blue}
              t={t}
              replace
            />
          </>
        )}
      </div>
    </main>
  );
}
